import { lazy, Suspense, useEffect } from 'react';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import ErrorState from '@zenbusiness/zui/ErrorState';
import Stack from '@zenbusiness/zui/Stack';

import CarouselStep from '#client/components/CarouselStep';
import ErrorBoundary from '#client/components/ErrorBoundary';
import useNextUpCards from '#client/hooks/useNextUpCardsContext';

import Card from './components/Card';
import NextUpCardsSkeleton from './components/NextUpCardsSkeleton';
import { WrapperStyled } from './NextUpCards.styles';
import SectionTitle from '../SectionTitle';

import useMapCards from './hooks/useMapCards';

const SwipeableViews = lazy(() => import('react-swipeable-views'));

function NextUpCards() {
  const { nextUpCards, activeStep, setActiveStep, loading } = useNextUpCards();
  const cards = useMapCards(nextUpCards);
  const maxSteps = cards.length;
  const hideStep = maxSteps === 1;

  const trackBannerDisplayed = useClickstreamCallback('bannerDisplayed');
  const trackButtonPressed = useClickstreamCallback('buttonPressed');
  const handleNext = () => {
    const nextStepIndex = activeStep === maxSteps - 1 ? 0 : activeStep + 1;
    setActiveStep(nextStepIndex);

    const {
      eventProps: { appPageName, bannerType, elementName }
    } = cards[nextStepIndex];

    trackButtonPressed({
      elementName: 'forward_arrow_onboarding_checklist',
      elementText: 'forward_arrow_onboarding_checklist',
      appPageName: 'active_banking_dashboard'
    });

    trackBannerDisplayed({
      bannerType,
      elementName,
      appPageName
    });
  };

  const handleBack = () => {
    setActiveStep(activeStep === 0 ? maxSteps - 1 : activeStep - 1);

    trackButtonPressed({
      elementName: 'backward_arrow_onboarding_checklist',
      elementText: 'backward_arrow_onboarding_checklist',
      appPageName: 'active_banking_dashboard'
    });
  };

  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  useEffect(() => {
    if (cards?.length > 0) {
      const {
        eventProps: { appPageName, bannerType, elementName }
      } = cards[0];

      trackBannerDisplayed({
        bannerType,
        elementName,
        appPageName
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards?.[0]?.type]);

  if (!cards.length) {
    return null;
  }

  if (loading) {
    return <NextUpCardsSkeleton />;
  }

  return (
    <Suspense fallback={<NextUpCardsSkeleton />}>
      <Stack
        gap={{ xs: '8px', md: '12px' }}
        mb={{ xs: '16px', md: '32px' }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
        >
          <SectionTitle>Next up</SectionTitle>
        </Stack>
        <ErrorBoundary
          fallback={
            <ErrorState
              slotProps={{
                primaryAction: {
                  eventProps: {
                    pageSection: 'banking'
                  }
                }
              }}
            />
          }
        >
          <WrapperStyled>
            <SwipeableViews
              enableMouseEvents
              index={activeStep}
              onChangeIndex={handleStepChange}
            >
              {cards.map((step, index) => (
                <div key={step.type}>
                  {Math.abs(activeStep - index) <= 2 ? (
                    <Card
                      actionButton={step.actionButton}
                      description={step.description}
                      disabled={step.disabled}
                      image={step.image}
                      loading={step.loading}
                      markdoneButton={step.markdoneButton}
                      removing={step.removing}
                      title={step.title}
                    />
                  ) : null}
                </div>
              ))}
            </SwipeableViews>
          </WrapperStyled>
          {!hideStep && (
            <CarouselStep
              activeStep={activeStep}
              handleBack={handleBack}
              handleNext={handleNext}
              maxSteps={maxSteps}
            />
          )}
        </ErrorBoundary>
      </Stack>
    </Suspense>
  );
}

export default NextUpCards;
